/** Client-side rate working calculations (mirrors backend concrete_estimator_rate.py). */

export const MATERIAL_GROUP_ORDER = [
    'cementitious',
    'coarse_aggregate',
    'fine_aggregate',
    'other',
];

export const MATERIAL_GROUP_LABELS = {
    cementitious: 'Cementitious',
    coarse_aggregate: 'Coarse Aggregate',
    fine_aggregate: 'Fine Aggregates',
    other: 'Others',
};

const CEMENTITIOUS_KEYS = ['cement', 'opc', 'ppc', 'flyash', 'ggbs', 'slag'];
const OTHER_KEYS = ['admix', 'admixture', 'water', 'additive'];
const COARSE_AGG_KEYS = ['20mm', '12mm', '40mm', '10mm', 'metal', 'jelly', 'coarse'];
const FINE_AGG_KEYS = ['csand', 'msand', 'rsand', 'sand', 'fineagg'];

export function normalizedMaterialName(name) {
    return String(name || '').toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
}

export function materialGroupForName(name) {
    const n = normalizedMaterialName(name);
    if (OTHER_KEYS.some((k) => n.includes(k.replace(/\s+/g, '')))) return 'other';
    if (CEMENTITIOUS_KEYS.some((k) => n.includes(k.replace(/\s+/g, '')))) return 'cementitious';
    if (COARSE_AGG_KEYS.some((k) => n.includes(k.replace(/\s+/g, '')))) return 'coarse_aggregate';
    if (FINE_AGG_KEYS.some((k) => n.includes(k.replace(/\s+/g, '')))) return 'fine_aggregate';
    return 'other';
}

export function sortMaterials(materials = []) {
    const order = Object.fromEntries(MATERIAL_GROUP_ORDER.map((key, idx) => [key, idx]));
    return [...materials].sort((a, b) => {
        const ga = order[a.material_group || materialGroupForName(a.product_name)] ?? 99;
        const gb = order[b.material_group || materialGroupForName(b.product_name)] ?? 99;
        if (ga !== gb) return ga - gb;
        return String(a.product_name || '').localeCompare(String(b.product_name || ''));
    });
}

export function groupMaterials(materials = []) {
    const sorted = sortMaterials(materials);
    const groups = [];
    let current = null;

    sorted.forEach((row) => {
        const group = row.material_group || materialGroupForName(row.product_name);
        const label = row.material_group_label || MATERIAL_GROUP_LABELS[group] || 'Others';
        if (!current || current.key !== group) {
            current = { key: group, label, rows: [] };
            groups.push(current);
        }
        current.rows.push({ ...row, material_group: group, material_group_label: label });
    });

    return groups;
}

export function num(value, defaultValue = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : defaultValue;
}

export function round2(value) {
    return Math.round(num(value) * 100) / 100;
}

const MTS_UNITS = ['mts', 'mt', 'ton', 'tonne', 't'];

export function isMtsUnit(unit) {
    return MTS_UNITS.includes(String(unit || '').toLowerCase().trim());
}

export function formatRateWorkingUnit(unit) {
    const u = String(unit || 'kg').toLowerCase().trim();
    if (u === 'kg' || u === 'kgs') return 'KGS';
    return unit || 'kg';
}

/** Rate working uses kg qty; convert from MTS inventory unit when needed. */
export function asKgRateWorkingRow(row, productUnit = '') {
    const unit = String(row.unit || productUnit || '').toLowerCase().trim();
    let qty = num(row.quantity);
    let grn = num(row.grn_avg_rate);
    let rate = num(row.rate);

    if (isMtsUnit(unit)) {
        qty *= 1000;
        grn = grn ? grn / 1000 : 0;
        rate = rate ? rate / 1000 : 0;
    } else if (isMtsUnit(productUnit)) {
        grn = grn ? grn / 1000 : 0;
        rate = rate ? rate / 1000 : 0;
    }

    const quantity = Math.round(qty * 1000) / 1000;
    const grnAvgRate = round2(grn);
    const rowRate = round2(rate);
    return {
        ...row,
        unit: 'kg',
        quantity,
        grn_avg_rate: grnAvgRate,
        rate: rowRate,
        amount: round2(quantity * rowRate),
    };
}

export function calculateRateTotals(sheet) {
    const materials = sortMaterials((sheet.materials || []).map((row) => ({
        ...row,
        material_group: row.material_group || materialGroupForName(row.product_name),
        material_group_label: row.material_group_label
            || MATERIAL_GROUP_LABELS[row.material_group || materialGroupForName(row.product_name)],
    })));

    const groupSubtotals = Object.fromEntries(MATERIAL_GROUP_ORDER.map((key) => [key, 0]));
    const materialSubtotal = Math.round(
        materials.reduce((sum, row) => {
            const qty = num(row.quantity);
            const rate = round2(row.rate);
            const amount = round2(qty * rate);
            row.grn_avg_rate = round2(row.grn_avg_rate);
            row.rate = rate;
            row.amount = amount;
            const group = row.material_group || 'other';
            groupSubtotals[group] = (groupSubtotals[group] || 0) + amount;
            return sum + amount;
        }, 0) * 100,
    ) / 100;

    const roundedGroupSubtotals = Object.fromEntries(
        Object.entries(groupSubtotals).map(([key, value]) => [key, Math.round(value * 100) / 100]),
    );

    const overhead = num(sheet.company_overhead);
    const transport = num(sheet.transport_charges);
    const costBeforeProfit = materialSubtotal + overhead + transport;

    const profitMode = sheet.profit_mode || 'percent';
    const profitValue = num(sheet.profit_value);
    const profitAmount = profitMode === 'amount'
        ? profitValue
        : Math.round(costBeforeProfit * profitValue / 100 * 100) / 100;

    const grossTotal = Math.round((costBeforeProfit + profitAmount) * 100) / 100;
    const taxPercent = num(sheet.tax_percent, 18);
    const taxAmount = Math.round(grossTotal * taxPercent / 100 * 100) / 100;
    const netTotal = Math.round((grossTotal + taxAmount) * 100) / 100;

    const volumeM3 = num(sheet.volume_m3);
    const ratePerM3 = volumeM3 > 0 ? Math.round(netTotal / volumeM3) : 0;

    return {
        ...sheet,
        materials,
        material_group_subtotals: roundedGroupSubtotals,
        material_subtotal: materialSubtotal,
        profit_amount: profitAmount,
        gross_total: grossTotal,
        tax_amount: taxAmount,
        net_total: netTotal,
        rate_per_m3: ratePerM3,
    };
}

export function findDefaultGst18Tax(taxList = []) {
    const gstTaxes = taxList.filter((t) => t.status && String(t.tax_type || '').toLowerCase() === 'gst');
    const match18 = gstTaxes.find((t) => {
        const total = num(t.tax_value1) + num(t.tax_value2);
        return Math.abs(total - 18) < 0.01;
    });
    return match18 || gstTaxes[0] || null;
}

export function taxPercentFromRecord(tax) {
    if (!tax) return 18;
    return num(tax.tax_value1) + num(tax.tax_value2);
}

export function formatTaxLabel(taxName, taxPercent) {
    const name = String(taxName || '').trim();
    if (name && /%/.test(name)) return name;
    const pct = num(taxPercent, 18);
    if (name) return `${name} @ ${pct}%`;
    return `GST @ ${pct}%`;
}

export function formatCurrency(value) {
    return num(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatRatePerM3(value) {
    return Math.round(num(value)).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

export const DEFAULT_VEHICLE_CAPACITY_M3 = 6;
export const DEFAULT_AVG_KM_PER_LTR = 2;

/** Transport per m³: (total km ÷ km/L) × diesel rate ÷ vehicle capacity (m³). */
export function computeTransportCharges(
    totalKm,
    dieselRate,
    vehicleCapacity = DEFAULT_VEHICLE_CAPACITY_M3,
    avgKmPerLtr = DEFAULT_AVG_KM_PER_LTR,
) {
    const km = num(totalKm);
    const diesel = num(dieselRate);
    const capacity = num(vehicleCapacity, DEFAULT_VEHICLE_CAPACITY_M3);
    const avgKm = num(avgKmPerLtr, DEFAULT_AVG_KM_PER_LTR);
    if (km <= 0 || diesel <= 0 || capacity <= 0 || avgKm <= 0) {
        return 0;
    }
    const litres = km / avgKm;
    const fuelCost = litres * diesel;
    return Math.round((fuelCost / capacity) * 100) / 100;
}
